import { ReactNode, useState } from 'react';
import { MenuItem } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useRouter } from 'next/router';
import { NETWORKS, POST_TYPES } from 'lib/constants';
import { Post } from 'lib/types';
import {
  buildPostPath, userIsPro, userIsAdmin,
  buildPostUrl,
  userIsProOrAdmin,
} from 'lib/helpers';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { DropdownMenu } from 'components/DropdownMenu';
import { useUpgradeModal } from 'components/UpgradeModal';
import { SaveToProjectListButton } from 'components/SaveToList';
import { ProIcon } from 'components/ProBadge/ProIcon';
import { PinMenuItem } from './PinMenuItem';

interface Props {
  post: Post;
  isProfileOwner?: boolean;
  showPin?: boolean;
  fetchData?: () => void;
  children?: ReactNode;
  projectId?: number;
}

export const PostCardMenu = ({
  post,
  isProfileOwner,
  showPin,
  fetchData,
  children,
  projectId,
}: Props) => {
  const currentUser = useCurrentUser();
  const API = useApi();
  const shouldUpgrade = !userIsPro(currentUser);
  const { showUpgradeModal } = useUpgradeModal();
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const networkName = NETWORKS[post.network].title;
  const postPath = buildPostPath(post);
  const [isRefreshEnqueued, setIsRefreshEnqueued] = useState(false);
  const isCreditedOnPost = post.credits?.find((credit) => credit.userId === currentUser?.id);

  const copyPostLink = async () => {
    const url = `https://gondola.cc${postPath}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: document.title,
          text: 'Check out this post on Gondola!',
          url,
        });
      } catch (err) {
        // noop
      }
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      enqueueSnackbar('Page copied!', { variant: 'success' });
    }
  };

  const setAsProjectCover = async () => {
    try {
      if (!projectId) {
        return;
      }
      await API.updateProject(projectId, { id: projectId, coverImgUrl: post.thumbnailUrl });
      enqueueSnackbar('Project cover updated!', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Error setting post as project cover', { variant: 'error' });
    }
  };

  const refreshPostStats = async () => {
    try {
      if (!userIsProOrAdmin(currentUser)) {
        showUpgradeModal('Upgrade to refresh post stats anytime you want');
        return;
      }

      // We set this to true to prevent the user from clicking the button again
      // (at least until the component re-renders)
      setIsRefreshEnqueued(true);
      await API.queueScrapePost(post.id);
      enqueueSnackbar('The post has been added to a post refresh queue. Reload the page in a few minutes to see the updated stats.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Error refreshing post stats', { variant: 'error' });
    }
  };

  const goTo = (href: string) => {
    router.push(href);
  };

  return (
    <DropdownMenu>
      <MenuItem onClick={() => goTo(postPath)}>
        View post details
      </MenuItem>
      {(post.type !== POST_TYPES.STORY || post.isLive) && (
        <MenuItem component="a" href={post.url} target="_blank" rel="noopenner noreferrer">
          View on
          {' '}
          {networkName}
        </MenuItem>
      )}
      <MenuItem component="a" href={`/visualmatch?url=${buildPostUrl(post)}`}>
        Compare to other post media
      </MenuItem>
      {projectId ? (
        <MenuItem onClick={setAsProjectCover}>
          Set as project cover
        </MenuItem>
      ) : (
        <MenuItem>
          <SaveToProjectListButton itemId={post.id} itemType="Post" text="Add to project" context="project" />
        </MenuItem>
      )}
      {userIsAdmin(currentUser) && (
        <MenuItem onClick={() => API.reindexPost(post.id)}>
          Reindex post (admin only)
        </MenuItem>
      )}

      <MenuItem onClick={copyPostLink}>
        Copy post link
      </MenuItem>

      {(isProfileOwner && !shouldUpgrade && isCreditedOnPost) && (
        <PinMenuItem
          postId={post.id}
          isPinned={showPin || false}
          shouldUpgrade={shouldUpgrade}
          onShowUpgrade={showUpgradeModal}
          onSuccess={fetchData}
        />
      )}
      <MenuItem onClick={refreshPostStats} disabled={isRefreshEnqueued}>
        Refresh post stats
        <ProIcon height={16} className="ml-2" />
      </MenuItem>
      {children}
    </DropdownMenu>
  );
};
